import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminGate, AdminShell, playHorn, getSoundPrefs, canAdmin } from "./AdminGate";
import { connectWS } from "../lib/ws";
import { toast } from "sonner";
import { Loader2, Bell, Volume2, VolumeX, CheckCheck, MessageCircle } from "lucide-react";

const STATUS_CHIP = {
  nova: ["🔴 Nova", "bg-red-100 text-red-600"],
  em_analise: ["🟡 Em análise", "bg-amber-100 text-amber-700"],
  aprovada: ["🟢 Aprovada", "bg-emerald-100 text-emerald-700"],
  lida: ["⚪ Lida", "bg-slate-100 text-slate-500"],
  resolvida: ["🔵 Resolvida", "bg-blue-100 text-blue-600"],
};
const EVENTO_LABEL = {
  novo_orcamento: "Nova solicitação/orçamento",
  novo_motorista: "Novo cadastro de motorista",
  vistoria: "Documentação/vistoria enviada",
  ocorrencia: "Ocorrência em corrida",
  corrida: "Corrida (aceite/andamento)",
  avaliacao: "Avaliações",
  info: "Outras",
};

function Notificacoes({ user }) {
  const [notifs, setNotifs] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [sound, setSound] = useState(getSoundPrefs());

  const load = () => {
    api.get("/admin/notificacoes").then((r) => setNotifs(r.data.notificacoes)).catch(() => setNotifs([]));
    api.get("/admin/notificacoes/config").then((r) => setCfg(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const closeWs = connectWS(() => load());
    return closeWs;
  }, []);

  const setStatus = async (n, status) => {
    try {
      await api.post(`/admin/notificacoes/${n.id}/status`, { status });
      load();
    } catch { toast.error("Falha ao atualizar"); }
  };

  const lerTodas = async () => {
    await api.post("/admin/notificacoes/ler-todas").catch(() => {});
    toast.success("Todas marcadas como lidas");
    load();
  };

  const saveSound = (s) => {
    setSound(s);
    localStorage.setItem("cjr_adm_sound", JSON.stringify(s));
  };

  const toggleEvento = async (ev) => {
    if (!cfg) return;
    const novo = { ...cfg.alerta_adm, eventos: { ...cfg.alerta_adm.eventos, [ev]: !(cfg.alerta_adm.eventos?.[ev] !== false) } };
    setCfg({ ...cfg, alerta_adm: novo });
    try {
      await api.put("/admin/notificacoes/config", { alerta_adm: novo });
      toast.success("Preferência salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Sem permissão"); load(); }
  };

  const saveWhats = async (ativo, numero) => {
    const novo = { ativo, numero_admin: numero };
    try {
      await api.put("/admin/notificacoes/config", { whatsapp_notif: novo });
      toast.success("WhatsApp atualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sem permissão"); }
  };

  return (
    <AdminShell user={user} title="Notificações e Alertas">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-notificacoes-page">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="alerta-som-config">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-extrabold text-navy uppercase tracking-wide flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-[#F5B301]" /> Alerta sonoro (buzina)
            </p>
            <button data-testid="som-toggle" onClick={() => saveSound({ ...sound, ativo: !sound.ativo })}
              className={`w-12 h-7 rounded-full transition-colors relative ${sound.ativo ? "bg-[#F5B301]" : "bg-slate-200"}`}>
              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${sound.ativo ? "left-6" : "left-1"}`} />
            </button>
          </div>
          <label className="text-[11px] font-semibold text-slate-500">Volume: {Math.round(sound.volume * 100)}%</label>
          <input data-testid="som-volume" type="range" min="0" max="1" step="0.05" value={sound.volume}
            onChange={(e) => saveSound({ ...sound, volume: parseFloat(e.target.value) })}
            className="w-full accent-[#F5B301] mt-1" />
          <button data-testid="som-testar" onClick={() => playHorn(sound.volume)}
            className="mt-2.5 w-full py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-bold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform">
            {sound.ativo ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} TESTAR BUZINA
          </button>
          {cfg && canAdmin(user, "notificacoes") && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Eventos que tocam a buzina</p>
              <div className="grid grid-cols-2 gap-1.5" data-testid="eventos-som">
                {Object.entries(EVENTO_LABEL).map(([k, l]) => (
                  <button key={k} data-testid={`evento-${k}`} onClick={() => toggleEvento(k)}
                    className={`py-2 rounded-xl border-2 text-[10px] font-bold text-left px-2.5 ${
                      cfg.alerta_adm.eventos?.[k] !== false ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 text-slate-400"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {cfg && canAdmin(user, "notificacoes") && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="whatsapp-config">
            <p className="text-xs font-extrabold text-navy uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <MessageCircle className="w-4 h-4 text-emerald-500" /> Notificação por WhatsApp
            </p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-600">Enviar alertas ao WhatsApp do admin</span>
              <button data-testid="whats-toggle" onClick={() => saveWhats(!cfg.whatsapp_notif?.ativo, cfg.whatsapp_notif?.numero_admin || "")}
                className={`w-12 h-7 rounded-full transition-colors relative ${cfg.whatsapp_notif?.ativo ? "bg-emerald-500" : "bg-slate-200"}`}>
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${cfg.whatsapp_notif?.ativo ? "left-6" : "left-1"}`} />
              </button>
            </div>
            <input data-testid="whats-numero" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" placeholder="Número com DDI (ex.: 5521986009971)"
              defaultValue={cfg.whatsapp_notif?.numero_admin || ""}
              onBlur={(e) => saveWhats(cfg.whatsapp_notif?.ativo || false, e.target.value)} />
            {!cfg.whatsapp_notif?.ativo && (
              <p className="text-[10px] text-amber-600 mt-2">
                Requer credenciais Twilio (WhatsApp Business) configuradas no servidor. Enquanto não configurado, os alertas seguem por painel, push e e-mail.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-xs font-extrabold text-navy uppercase tracking-wide">Central de notificações</p>
          <button data-testid="ler-todas-btn" onClick={lerTodas}
            className="text-[11px] font-bold text-[#F5B301] flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" /> Marcar todas como lidas
          </button>
        </div>

        {notifs === null && <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>}
        {notifs !== null && notifs.length === 0 && <p className="text-center text-sm text-slate-400 py-10" data-testid="notifs-empty">Nenhuma notificação.</p>}
        {(notifs || []).map((n) => {
          const [label, cls] = STATUS_CHIP[n.status] || STATUS_CHIP.nova;
          return (
            <div key={n.id} className={`bg-white rounded-2xl border shadow-sm p-3.5 ${n.lida ? "border-slate-100" : "border-[#F5B301]/60"}`} data-testid={`notif-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className={`text-xs leading-snug flex-1 ${n.lida ? "text-slate-500" : "text-navy font-semibold"}`}>{n.texto}</p>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cls}`} data-testid={`notif-status-${n.id}`}>{label}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">{new Date(n.em).toLocaleString("pt-BR")}</p>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {n.link && (
                  <a href={n.link} data-testid={`notif-link-${n.id}`} className="px-2.5 py-1.5 rounded-lg bg-navy text-[#F5B301] text-[10px] font-bold">ABRIR</a>
                )}
                {n.status === "nova" && (
                  <button data-testid={`notif-analise-${n.id}`} onClick={() => setStatus(n, "em_analise")}
                    className="px-2.5 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-bold">EM ANÁLISE</button>
                )}
                {!n.lida && (
                  <button data-testid={`notif-lida-${n.id}`} onClick={() => setStatus(n, "lida")}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-bold">MARCAR LIDA</button>
                )}
                {n.status !== "resolvida" && (
                  <button data-testid={`notif-resolvida-${n.id}`} onClick={() => setStatus(n, "resolvida")}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-100 text-blue-600 text-[10px] font-bold">RESOLVIDA</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
}

export default function AdminNotifications() {
  return <AdminGate>{(user) => <Notificacoes user={user} />}</AdminGate>;
}
